import React, { useEffect, useState } from "react";
import { X, AlertTriangle, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { TAGS, FIELD_META, SELLER_TAG_OPTIONS } from "../lib/stages";
import { useStages } from "../lib/useStages";

export default function StageTransitionDialog({ open, lead, onClose, onMoved }) {
  const { stages: STAGES, taggingRequired: STAGES_REQUIRING_TAG } = useStages();
  const [stage, setStage] = useState(lead?.lead_stage || "New");
  const [tag, setTag] = useState(lead?.stage_tagging || "");
  const [reason, setReason] = useState("");
  const [reasons, setReasons] = useState([]);
  const [missingKeys, setMissingKeys] = useState([]); // field keys requested by backend
  const [inlineEdits, setInlineEdits] = useState({}); // { key: value }
  const [masters, setMasters] = useState({});
  const [saving, setSaving] = useState(false);

  // Reset state on open
  useEffect(() => {
    if (!open) return;
    setStage(lead?.lead_stage || "New");
    setTag(lead?.stage_tagging || "");
    setReason("");
    setMissingKeys([]);
    setInlineEdits({});
    (async () => {
      try {
        const [r, m] = await Promise.all([
          api.get("/masters?type=lost_reason&active_only=true"),
          api.get("/masters?active_only=true"),
        ]);
        setReasons(r.data);
        const grouped = {};
        m.data.forEach((it) => {
          grouped[it.type] = grouped[it.type] || [];
          grouped[it.type].push(it);
        });
        setMasters(grouped);
      } catch (err) {
        console.warn("Failed to load stage masters/reasons:", err);
      }
    })();
  }, [open, lead]);

  if (!open) return null;

  const tagRequired = STAGES_REQUIRING_TAG.has(stage);
  const reasonRequired = stage === "Not-Interested";
  const showReasonDropdown = stage === "Not-Interested" || tag === "Lost";

  const updateField = (key, value) => setInlineEdits((p) => ({ ...p, [key]: value }));

  const renderFieldInput = (key) => {
    const meta = FIELD_META[key] || { label: key, type: "text" };
    const current = inlineEdits[key] !== undefined ? inlineEdits[key] : lead?.[key] ?? "";

    if (meta.type === "sellertag") {
      return (
        <select
          data-testid={`stm-field-${key}`}
          value={current === null || current === undefined ? "" : current}
          onChange={(e) => updateField(key, e.target.value === "" ? null : Number(e.target.value))}
          className="stm-input"
        >
          <option value="">Select seller tag</option>
          {SELLER_TAG_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    if (meta.type === "master") {
      const opts = masters[meta.masterKey] || [];
      return (
        <select
          data-testid={`stm-field-${key}`}
          value={current || ""}
          onChange={(e) => updateField(key, e.target.value)}
          className="stm-input"
        >
          <option value="">Select…</option>
          {opts.map((o) => <option key={o.id} value={o.value}>{o.value}</option>)}
        </select>
      );
    }
    if (meta.type === "multimaster") {
      const opts = masters[meta.masterKey] || [];
      const arr = Array.isArray(current) ? current : [];
      return (
        <div className="flex flex-wrap gap-1.5">
          {opts.map((o) => {
            const active = arr.includes(o.value);
            return (
              <button
                type="button"
                key={o.id}
                data-testid={`stm-multi-${key}-${o.value.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() =>
                  updateField(
                    key,
                    active ? arr.filter((x) => x !== o.value) : [...arr, o.value],
                  )
                }
                className={`px-2.5 py-1 text-xs font-medium rounded-full border ${
                  active
                    ? "bg-orange-500 text-white border-orange-500"
                    : "bg-white text-slate-700 border-slate-200 hover:border-orange-300"
                }`}
              >
                {o.value}
              </button>
            );
          })}
          {opts.length === 0 && <span className="text-xs text-slate-400">No options configured</span>}
        </div>
      );
    }
    if (meta.type === "number") {
      return (
        <input
          data-testid={`stm-field-${key}`}
          type="number"
          value={current === null ? "" : current}
          onChange={(e) => updateField(key, e.target.value === "" ? null : Number(e.target.value))}
          className="stm-input"
        />
      );
    }
    if (meta.type === "month") {
      return (
        <input
          data-testid={`stm-field-${key}`}
          type="month"
          value={current || ""}
          onChange={(e) => updateField(key, e.target.value)}
          className="stm-input"
        />
      );
    }
    if (meta.type === "textarea") {
      return (
        <textarea
          data-testid={`stm-field-${key}`}
          rows={2}
          value={current || ""}
          onChange={(e) => updateField(key, e.target.value)}
          className="stm-input"
        />
      );
    }
    return (
      <input
        data-testid={`stm-field-${key}`}
        type="text"
        placeholder={meta.placeholder || ""}
        value={current || ""}
        onChange={(e) => updateField(key, e.target.value)}
        className="stm-input"
      />
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Save any inline edits first (single PATCH)
      if (Object.keys(inlineEdits).length > 0) {
        await api.patch(`/leads/${lead.id}`, inlineEdits);
      }
      const body = { to_stage: stage };
      if (tagRequired) body.tag = tag || null;
      if (showReasonDropdown) body.lost_reason = reason || null;
      await api.post(`/leads/${lead.id}/stage`, body);
      toast.success(`Moved to ${stage}`);
      onMoved?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === "object" && Array.isArray(d.missing_field_keys)) {
        setMissingKeys(d.missing_field_keys);
        toast.error(d.detail || "Fill the highlighted fields below");
      } else if (d && typeof d === "object" && d.missing_fields) {
        // Backwards compatibility — convert labels to keys via best-effort lookup
        const reverse = Object.fromEntries(
          Object.entries(FIELD_META).map(([k, v]) => [v.label, k])
        );
        setMissingKeys(d.missing_fields.map((l) => reverse[l] || l));
        toast.error(d.detail || "Fill the highlighted fields below");
      } else {
        toast.error(formatApiError(d) || "Failed to move stage");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="stage-transition-dialog"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        <style>{`.stm-input{width:100%;padding:.5rem .65rem;border:1px solid rgb(226 232 240);border-radius:.375rem;font-size:.85rem;background:white;outline:none}.stm-input:focus{border-color:rgb(249 115 22);box-shadow:0 0 0 2px rgba(249,115,22,.2)}`}</style>
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Move stage</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {lead?.company_name} — current: <b>{lead?.lead_stage}</b>
              {lead?.stage_tagging && <span> · <b>{lead.stage_tagging}</b></span>}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Target stage</label>
            <select
              data-testid="stage-transition-target"
              value={stage}
              onChange={(e) => {
                const next = e.target.value;
                setStage(next);
                setMissingKeys([]);
                if (next !== "Not-Interested" && tag !== "Lost") setReason("");
              }}
              className="stm-input"
            >
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {tagRequired && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Tag *</label>
              <div className="flex gap-2">
                {TAGS.map((t) => (
                  <button
                    type="button"
                    key={t}
                    data-testid={`stage-transition-tag-${t.toLowerCase()}`}
                    onClick={() => {
                      setTag(t);
                      if (t !== "Lost" && stage !== "Not-Interested") setReason("");
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                      tag === t ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-700 border-slate-200"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}
          {showReasonDropdown && (
            <div data-testid="stm-lost-reason-block">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                Lost / Not Int reason {reasonRequired ? "*" : <span className="text-slate-400 normal-case tracking-normal">(optional but recommended)</span>}
              </label>
              <select
                data-testid="stage-transition-lost-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="stm-input"
              >
                <option value="">Select reason…</option>
                {reasons.map((r) => <option key={r.id} value={r.value}>{r.value}</option>)}
              </select>
            </div>
          )}
          {missingKeys.length > 0 && (
            <div data-testid="stm-missing-section" className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-800">
                  <div className="font-semibold mb-0.5">Mandatory for “{stage}”</div>
                  <div className="text-xs text-amber-700">Fill these fields below and click <b>Save & move</b> — we'll save them to the lead and move the stage in one go.</div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {missingKeys.map((k) => (
                  <div key={k} data-testid={`stm-missing-${k}`}>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      {FIELD_META[k]?.label || k} <span className="text-red-500">*</span>
                    </label>
                    {renderFieldInput(k)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-2 bg-slate-50/50">
          <span className="text-xs text-slate-500">
            {Object.keys(inlineEdits).length > 0 && `${Object.keys(inlineEdits).length} field(s) edited`}
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-slate-200 bg-white">Cancel</button>
            <button
              type="submit"
              data-testid="stage-transition-submit"
              disabled={saving}
              className="px-4 py-2 text-sm rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-medium inline-flex items-center gap-1.5"
            >
              <Save size={13} />
              {saving ? "Saving…" : (Object.keys(inlineEdits).length > 0 ? "Save & move stage" : "Move stage")}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
